<template>
  <div class="table-scroll-container">
    <b-table
      v-if="hasPermission('saving-plan.list')"
      :data="data"
      :loading="loading"
      paginated
      backend-pagination
      :total="total"
      :per-page="perPage"
      :current-page="page"
      @page-change="onPageChange"
      aria-next-label="Nächste Seite ->"
      aria-previous-label="<- Vorherige Seite"
      aria-page-label="Seite"
      aria-current-label="Aktuelle Seite"
      backend-sorting
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      @sort="onSort"
    >
      <b-table-column
        v-if="showAtOverview"
        label="Kundennummer"
        field="customer_id"
        sortable
        v-slot="props"
      >
      <template v-if="hasPermission('customer.get')">
        <router-link
          :to="{
            name: 'client.edit',
            params: { id: props.row.customer.customer_id },
          }"
        >
          {{ props.row.customer.customer_number }}
        </router-link>
      </template>
      <template v-else>
        {{ props.row.customer.customer_number }}
      </template>
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Kundenname"
        field="customer_name"
        v-slot="props"
      >
      <template v-if="hasPermission('customer.get')">
        <router-link
          :to="{
            name: 'client.edit',
            params: { id: props.row.customer.customer_id },
          }"
        >
          {{ props.row.customer.first_name }} {{ props.row.customer.last_name }}
        </router-link>
      </template>
      <template v-else>
        {{ props.row.customer.first_name }} {{ props.row.customer.last_name }}
      </template>
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Personennummer"
        field="personnumber"
        v-slot="props"
      >
        {{ props.row.personnumber }}
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Beratername"
        field="employee_name"
        v-slot="props"
      >
        {{ props.row.employee.full_name }}
      </b-table-column>

      <b-table-column
        v-if="showAtOverview"
        label="Kundenberaternummer"
        field="employee_number"
        v-slot="props"
      >
        {{ props.row.employee.employee_number }}
      </b-table-column>
      <b-table-column label="Sparplannummer" field="id" sortable v-slot="props">
        {{ props.row.saving_plan_number }}
      </b-table-column>
      <b-table-column
        label="Provisionssatz in %"
        field="id"
        sortable
        v-slot="props"
      >
        {{
          props.row.customer.commission !== 0 &&
          props.row.customer.commission !== null &&
          props.row.customer.commission !== undefined
            ? props.row.customer.commission - 2
            : props.row.bank.commission - 2
        }}
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Bank ID"
        field="bank_id"
        v-slot="props"
      >
        {{ props.row.bank.bank_id }}
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Bankname"
        field="bank_name"
        v-slot="props"
      >
        {{ props.row.bank.bank_name }}
      </b-table-column>
      <b-table-column label="Produkt" field="product" v-slot="props">
        {{ props.row.product.name }}
      </b-table-column>
      <b-table-column label="Sparrate" field="amount" sortable v-slot="props">
        {{
          props.row.amount.toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
          })
        }}
      </b-table-column>
      <b-table-column
        label="Tag der Abrechnung"
        field="day_of_bill"
        sortable
        v-slot="props"
      >
        {{ props.row.day_of_bill ? props.row.day_of_bill + '.' : '' }}
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Aktiv"
        field="active"
        sortable
        v-slot="props"
      >
        {{ props.row.active_translated }}
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Bestand (Stück)"
        field="vault_amount"
        v-slot="props"
      >
        {{
          new Intl.NumberFormat('de-DE', {
            style: 'decimal',
            minimumFractionDigits: 5,
            maximumFractionDigits: 5,
          }).format(props.row.vault_amount)
        }}
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Verkaufswert (€)"
        field="value"
        v-slot="props"
      >
        {{
          props.row.value.toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
          })
        }}
      </b-table-column>
      <b-table-column
        v-if="showAtOverview"
        label="Anlage Datum"
        field="created_at"
        sortable
        v-slot="props"
      >
        {{ props.row.created_at }}
      </b-table-column>
      <b-table-column
        v-if="this.$route.name !== 'saving-plan.list'"
        label="Erstellt am"
        field="created_at"
        sortable
        v-slot="props"
      >
        {{ props.row.created_at }}
      </b-table-column>
      <b-table-column
        v-if="this.$route.name !== 'saving-plan.list'"
        label="Geändert am"
        field="updated_at"
        sortable
        v-slot="props"
      >
        {{ props.row.updated_at }}
      </b-table-column>
      <b-table-column
        v-if="this.$route.name !== 'saving-plan.list'"
        label="Status"
        field="active"
        sortable
        v-slot="props"
      >
        {{ props.row.active ? 'Aktiv' : 'Nicht aktiv' }}
      </b-table-column>
      <b-table-column
        v-if="
          $route.name !== 'saving-plan.list' &&
          (
            hasPermission('saving-plan.put') ||
            hasPermission('saving-plan.freeze') ||
            hasPermission('saving-plan.thaw') ||
            hasPermission('saving-plan.contract')
          )
        "
        custom-key="actions"
        cell-class="is-actions-cell"
        v-slot="props"
      >
        <div class="buttons is-right">
          <router-link
            v-if="hasPermission('saving-plan.put')"
            :to="'/saving-plan/' + props.row.id"
            class="button"
            type="button"
          >
            <b-icon icon="tooltip-edit" />
            <span>Bearbeiten</span>
          </router-link>
          <template
            v-if="
              hasPermission('saving-plan.freeze') &&
              hasPermission('saving-plan.thaw')
            "
          >
            <button
              v-if="props.row.active"
              class="button"
              type="button"
              @click.prevent="freeze(props.row)"
              @mouseenter="showStandingOrderReminder()"
              @mouseleave="hideStandingOrderReminder()"
            >
              <b-icon icon="snowflake" />
              <span>Beenden</span>
            </button>
            <button
              v-else
              class="button"
              type="button"
              @click.prevent="thaw(props.row)"
              @mouseenter="showStandingOrderReminder()"
              @mouseleave="hideStandingOrderReminder()"
            >
              <b-icon icon="snowflake-melt" />
              <span>Fortsetzen</span>
            </button>
            <div
              id="standing-order-reminder"
              class="reminder-window"
              style="display: none"
            >
              <div v-if="props.row.active">
                Bitte denken Sie daran ggf. den dazugehörigen Dauerauftrag zu
                beenden
              </div>
              <div v-else>
                Bitte denken Sie daran ggf. den Dauerauftrag entsprechend
                einzurichten
              </div>
            </div>
          </template>
          <button
            v-if="hasPermission('saving-plan.contract')"
            class="button"
            type="button"
            @click.prevent="showPdf(props.row.id)"
          >
            <b-icon icon="file-pdf-box" />
            <span>PDF anzeigen</span>
          </button>
          <button
            v-if="hasPermission('saving-plan.contract')"
            class="button"
            type="button"
            @click.prevent="printPdf(props.row.id)"
          >
            <b-icon icon="file-pdf-box" />
            <span>PDF drucken</span>
          </button>
        </div>
      </b-table-column>

      <section slot="empty" class="section">
        <div class="content has-text-grey has-text-centered">
          <template v-if="loading">
            <p>
              <b-icon icon="dots-horizontal" size="is-large" />
            </p>
            <p>Lade Daten...</p>
          </template>
          <template v-else>
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p>Keine Daten vorhanden&hellip;</p>
          </template>
        </div>
      </section>
    </b-table>
  </div>
</template>
<script>
import { oaxios } from '@/_helpers'
import printJS from 'print-js'
import { mapGetters } from 'vuex'

export default {
  name: 'saving-plan-list',
  props: {
    customerId: {
      type: Number,
    },
    search: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      data: [],
      total: 0,
      loading: false,
      sortField: 'customer_id',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      page: 1,
      perPage: 15,
    }
  },
  computed: {
    ...mapGetters('permission', ['hasPermission']),
    showAtOverview() {
      if (this.$route.name === 'saving-plan.list') {
        return true
      } else return false
    },
  },
  watch: {
    search() {
      this.page = 1
      this.loadAsyncData()
    },
  },
  methods: {
    loadAsyncData() {
      if (this.hasPermission('saving-plan.list')) {
        this.loading = true
        let data = {
          customer_id: this.customerId,
          search: this.search,
          sort_field: this.sortField,
          sort_order: this.sortOrder,
          get_vault: true,
          get_employee: true,
        }

        if (this.$route.name !== 'saving-plan.list') {
          data = {
            customer_id: this.customerId,
            search: this.search,
            sort_field: this.sortField,
            sort_order: this.sortOrder,
            get_vault: false,
            get_employee: false,
          }
        }

        oaxios(`/saving-plan/list?page=${this.page}`, data)
          .then((r) => {
            this.data = r.data.data
            this.total = r.data.meta.total
            this.perPage = r.data.meta.per_page
          })
          .catch(() => {
            this.data = []
            this.total = 0
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    showStandingOrderReminder() {
      document.getElementById('standing-order-reminder').style.display = 'block'
    },
    hideStandingOrderReminder() {
      document.getElementById('standing-order-reminder').style.display = 'none'
    },
    onPageChange(page) {
      this.page = page
      this.loadAsyncData()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadAsyncData()
    },
    freeze(obj) {
      if (this.hasPermission('saving-plan.freeze')) {
        this.loading = true

        oaxios('/saving-plan/freeze', { saving_plan_id: obj.id })
          .then(() => {
            obj.active = false
            this.$buefy.toast.open({
              message: `Der Sparplan wurde erfolgreich beendet.`,
              type: 'is-success',
              queue: false,
            })
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false
          })
      }
    },
    thaw(obj) {
      if (this.hasPermission('saving-plan.thaw')) {
        this.loading = true

        oaxios('/saving-plan/thaw', { saving_plan_id: obj.id })
          .then(() => {
            obj.active = true
            this.$buefy.toast.open({
              message: `Der Sparplan wurde erfolgreich fortgesetzt.`,
              type: 'is-success',
              queue: false,
            })
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false
          })
      }
    },
    showPdf(id) {
      if (this.hasPermission('saving-plan.contract')) {
        this.submitting = true
        const data = {
          saving_plan_id: id,
        }
        oaxios('saving-plan/contract', data, 'post', 'blob')
          .then((r) => {
            const reader = new FileReader()
            reader.readAsDataURL(r.data)
            reader.onloadend = () => {
              const base64data = reader.result
              const w = window.open('about:blank', '_blank')
              w.document.write(
                "<html lang='de'><head><title>Vertrag</title></head><body style='margin: 0; overflow: hidden'><iframe style='width: 100%; height: 100%' src='" +
                  base64data +
                  "'></iframe></body></html>",
              )
            }
          })
          .catch(() => {})
          .finally(() => {
            this.submitting = false
          })
      }
    },
    printPdf(id) {
      if (this.hasPermission('saving-plan.contract')) {
        this.submitting = true
        const data = {
          saving_plan_id: id,
        }
        oaxios('saving-plan/contract', data, 'post', 'blob')
          .then((r) => {
            const reader = new FileReader()
            reader.readAsDataURL(r.data)
            reader.onloadend = () => {
              const base64data = reader.result
              printJS({
                printable: base64data.replace(
                  'data:application/pdf;base64,',
                  '',
                ),
                type: 'pdf',
                base64: true,
                showModal: true,
                modalMessage: 'Lade PDF',
              })
            }
          })
          .catch(() => {})
          .finally(() => {
            this.submitting = false
          })
      }
    },
  },
  mounted() {
    this.loadAsyncData()
  },
}
</script>
